// =============================================================================
// DISTILLED PROFILE — the canonical "true CV", the distillation keystone.
//
// The middle layer between the raw corpus (roles, achievements, dna_facets) and
// every downstream use (CV render, job tailoring). Capture produces facets and a
// narrative through-line (dna_profiles); this DISTILS that material into one
// ranked, gold-first, canonical artifact the tailoring engines can stand on,
// instead of each job re-deriving "who is this person" from raw rows.
//
// DERIVED but OVERRIDABLE (the user_profiles pattern): a derive pass ranks the
// corpus; anything the user pins or re-orders is recorded in `overrides` and the
// next derive honours it. VERSIONED (the dna_profiles pattern): each derive is a
// new row, the highest version wins, history kept for provenance.
//
// PROTECTED-zone note: this is ADDITIVE. It only READS the locked DNA/corpus
// tables and writes its own table; it does not modify the capture subsystem.
// See docs/corpus-distillation-plan.md (Phase B).
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, text, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** A ranked reference to a live corpus row (a role or an achievement). We store
 *  a reference, not a copy, so editing the source card upstream is reflected,
 *  never stale. `isGold` mirrors the dna_facets.isNormalToMe signal; `pinned`
 *  marks a user override the next derive must preserve. */
export interface RankedRef {
  /** roles.id or achievements.id. */
  refId: string;
  /** 0-based position in the ranked list (0 = lead). */
  rank: number;
  /** True when this is "normal to me" gold (curse-of-expertise capture). */
  isGold: boolean;
  /** True when the user pinned this position; protected from re-derive. */
  pinned?: boolean;
}

// ── The canonical distilled profile. Current row per user = highest version. ──
export const distilledProfiles = pgTable(
  'distilled_profiles',
  {
    id: id(),
    userId: text('user_id').notNull(),
    // Provenance: the dna_profiles row this was distilled from (its through-line
    // was the ranking lens). Null when distilled before any synthesis exists.
    sourceProfileId: uuid('source_profile_id'),
    // The canonical positioning spine: inherited from dna_profiles, may be
    // user-overridden. The one line the CV + cover letters stand on.
    spine: text('spine'),
    // Roles ranked by how strongly they prove the through-line, lead first.
    rankedRoles: jsonb('ranked_roles').$type<RankedRef[]>().default(sql`'[]'::jsonb`),
    // Achievements ranked gold-first, then by through-line relevance.
    goldFirstAchievements: jsonb('gold_first_achievements')
      .$type<RankedRef[]>()
      .default(sql`'[]'::jsonb`),
    // Consolidated skill names, ranked.
    skills: jsonb('skills').$type<string[]>().default(sql`'[]'::jsonb`),
    // Field/slot names the user has overridden, protected from the next derive
    // (the user_profiles pattern). e.g. 'spine', or 'pin:<refId>'.
    overrides: jsonb('overrides').$type<string[]>().default(sql`'[]'::jsonb`),
    // Bumped each derive; resolve reads the highest version (dna_profiles pattern).
    version: integer('version').default(1).notNull(),
    modelId: varchar('model_id', { length: 64 }),
    // GATE-1 (GenerationGate.ts, Phase B): the through-line's own verdict — `spine` is
    // the ONE piece of free narrative this table carries (roles/achievements are
    // reference ids, already reconciled against the real corpus below), so it is the
    // slot a company/figure could be misattributed in (his 7/8 D2 finding). Null when
    // spine is null (nothing to gate) or before this lane's first derive. Additive
    // (rule #11) — a pre-existing row simply has no report until its next derive.
    groundingReport: jsonb('grounding_report'),
    ...timestamps,
  },
  (t) => ({ userIdx: index('distilled_profiles_user_idx').on(t.userId) }),
);
